import React, { Fragment, Component, createContext } from 'react';
import { Router, navigate } from '@reach/router';
import { AnimatePresence } from 'framer-motion';

import axiosUI from './axiosUI';
import Homepage from './components/Homepage/Homepage';
import Translations from './components/Translations/Translations';
import TranslationsPerMarket from './components/Translations/TranslationsPerMarket';
import TranslationChangeset from './components/Translations/TranslationChangeset';
import BannersPerMarket from './components/Banners/BannersPerMarket';
import BannerChangeset from './components/Banners/BannerChangeset';
import Banners from './components/Banners/Banners';
import Channeling from './components/Channeling/Channeling';
import OpeningHoursPerMarket from './components/Channeling/OpeningHoursPerMarket';
import OpeningHourChangeset from './components/Channeling/OpeningHourChangeset';
import ChannelingChangeset from './components/Channeling/ChannelingChangeset';
import ChannelingPerMarket from './components/Channeling/ChannelingPerMarket';
import ToggleChannelingChangeset from './components/Channeling/ToggleChannelingChangeset';
import StatsPage from './components/StatsPage/StatsPage';
import Settings from './components/Settings/Settings';
import LoginPage from './components/LoginPage/LoginPage';
import Notification from './components/Notification/Notification';
import MarketGroup from './components/MarketGroup';
import Loader from './components/Loader/Loader';

export const AppContext = createContext();

class ScrollToTop extends Component {
  componentDidUpdate(prevProps, prevState) {
    if (this.props.location.href !== prevProps.location.href) {
      window.scrollTo(0, 0);
    }
  }
  render() {
    return <Fragment>{this.props.children}</Fragment>;
  }
}

class App extends Component {
  constructor(props) {
    super(props);

    let savedCountry = '';
    let savedLanguage = '';
    let shrinkSidebar = false;

    if (window.localStorage) {
      const lastSelectedMarket =
        window.localStorage.getItem('lastSelectedMarket');
      if (lastSelectedMarket) {
        const marketProps = lastSelectedMarket.split(',');
        savedCountry = marketProps[0];
        savedLanguage = marketProps[1];
      }

      shrinkSidebar = window.localStorage.getItem('shrinkSidebar') === 'true';
    }

    this.state = {
      shrinkSidebar,
      notificationMessage: '',
      notificationType: 'ERROR',
      savedCountry,
      savedLanguage,
      isAuth: false,
      loading: false,
    };
  }

  componentDidMount = async () => {
    const pathname = window.location.pathname;
    if (pathname === '/login' || this.state.isAuth) {
      return;
    }

    this.setState({ loading: true });

    try {
      const url = '/api/auth/session';
      const { data } = await axiosUI.get(url);
      if (data) {
        this.setState({ isAuth: true });
      } else {
        navigate('/login');
      }
    } catch (error) {
      navigate('/login');
    }

    this.setState({ loading: false });
  };

  createNotification = (config) => {
    const { type, message, timeout = 2000 } = config;
    this.setState(
      { notificationMessage: message, notificationType: type },
      () => {
        setTimeout(() => {
          this.dismissNotification();
        }, timeout);
      }
    );
  };

  dismissNotification = () => {
    this.setState({ notificationMessage: '' });
  };

  toggleSidebar = () => {
    this.setState(
      (prevState) => ({ shrinkSidebar: !prevState.shrinkSidebar }),
      () => {
        if (window.localStorage) {
          window.localStorage.setItem(
            'shrinkSidebar',
            this.state.shrinkSidebar
          );
        }
      }
    );
  };

  updateMarket = (config, callback) => {
    this.setState(config, callback);

    if (window.localStorage) {
      const lastSelectedMarket = `${config.savedCountry},${config.savedLanguage}`;
      window.localStorage.setItem('lastSelectedMarket', lastSelectedMarket);
    }
  };

  render() {
    return (
      <AppContext.Provider
        value={{
          shrinkSidebar: this.state.shrinkSidebar,
          toggleSidebar: this.toggleSidebar,
          createNotification: this.createNotification,
          savedLanguage: this.state.savedLanguage,
          savedCountry: this.state.savedCountry,
          updateMarket: this.updateMarket,
        }}>
        <div className="app">
          <Loader loading={this.state.loading}>
            <Router>
              <ScrollToTop path="/">
                <LoginPage path="login" />
                <Homepage path="/" />
                <Translations path="translations">
                  <MarketGroup
                    path="/"
                    highlightNavItemIndex={1}
                    title="Translations"
                  />
                  <TranslationsPerMarket path=":country/:language" />
                  <TranslationChangeset path=":country/:language/changeset" />
                </Translations>
                <Banners path="banners">
                  <MarketGroup
                    path="/"
                    highlightNavItemIndex={2}
                    title="Banners"
                  />
                  <BannersPerMarket path=":country/:language" />
                  <BannerChangeset path=":country/:language/changeset" />
                </Banners>
                <Channeling path="channeling">
                  <MarketGroup
                    path="/"
                    highlightNavItemIndex={3}
                    title="Channeling"
                  />
                  <ChannelingPerMarket path=":country/:language" />
                  <ChannelingChangeset path=":country/:language/changeset" />
                  <OpeningHoursPerMarket path=":country/:language/opening-hours" />
                  <OpeningHourChangeset path=":country/:language/opening-hours/changeset" />
                  <ToggleChannelingChangeset path="changeset" />
                </Channeling>
                <StatsPage path="stats" />
                <Settings path="settings" />
              </ScrollToTop>
            </Router>
            <AnimatePresence>
              {this.state.notificationMessage && (
                <Notification
                  type={this.state.notificationType}
                  message={this.state.notificationMessage}
                />
              )}
            </AnimatePresence>
          </Loader>
        </div>
      </AppContext.Provider>
    );
  }
}

export function withGlobalContext(Component) {
  return function WrapperComponent(props) {
    return (
      <AppContext.Consumer>
        {(context) => <Component {...props} {...context} />}
      </AppContext.Consumer>
    );
  };
}

export default  App;
